// File: src/pages/buyer/AddProduct/sections/BidsInformation.jsx
import React from "react";
import { useFormContext, Controller, useWatch } from "react-hook-form";
import { Row, Col, Form } from "react-bootstrap";
import {
  formatWithCommasAndDecimals,
  parseRawNumber,
} from "../../../../utils/price";

const OfferPricing = ({ isMetal = true }) => {
  const {
    control,
    register,
    formState: { errors },
  } = useFormContext();

  const calcUnit = useWatch({ control, name: "calc_unit" });
  const currency = useWatch({ control, name: "currency" });
  const pricePerUnitVal = useWatch({ control, name: "price_per_unit" });

  const getDynamicLabel = (baseLabel, showPricePerUnit = false) => {
    if (!calcUnit || !currency) return baseLabel;
    const ratePart =
      showPricePerUnit && pricePerUnitVal
        ? `Price per unit: ${formatWithCommasAndDecimals(pricePerUnitVal)} `
        : "";
    return (
      <>
        {baseLabel}
        <span
          className="text-muted ms-2"
          style={{ fontWeight: "400", fontSize: "0.85rem" }}
        >
          ({ratePart}
          {currency}/{calcUnit})
        </span>
      </>
    );
  };

  return (
    <div className="border rounded p-4 mb-4 bg-white">
      <h5 className="fw-semibold mb-4">Offer Pricing</h5>

      {/* Commented out for future use: Reverse Auction switcher */}
      {/* 
      <Row className="mb-4">
        <Col>
          <Form.Label className="d-block">Reverse Auction</Form.Label>
          <Controller
            name="is_reverse_auction"
            control={control}
            render={({ field }) => (
              <Form.Switch
                id="is-reverse-auction"
                label="Enable Reverse Auction"
                checked={field.value}
                onChange={(e) => field.onChange(e.target.checked)}
              />
            )}
          />
        </Col>
      </Row>
      */}


      {/* Bid Details */}

      {!isMetal && (
        <>
          <Row className="g-3">
            {/* Unit */}
            <Col md={3}>
              <Form.Group>
                <Form.Label>Unit</Form.Label>
                <Controller
                  name="calc_unit"
                  control={control}
                  render={({ field }) => (
                    <Form.Select {...field}>
                      <option value="">Select</option>
                      <option value="toz">Troy Ounce</option>
                      <option value="mt">Metric Tonne</option>
                      <option value="kg">Kg</option>
                      <option value="g">Gram</option>
                    </Form.Select>
                  )}
                />
                {errors.calc_unit && (
                  <div className="text-danger small">{errors.calc_unit.message}</div>
                )}
              </Form.Group>
            </Col>

            {/* Currency */}
            <Col md={3}>
              <Form.Group>
                <Form.Label>Currency</Form.Label>
                <Controller
                  name="currency"
                  control={control}
                  render={({ field }) => (
                    <Form.Select {...field}>
                      <option value="">Select Currency</option>
                      <option value="USD">USD</option>
                      <option value="AUD">AUD</option>
                    </Form.Select>
                  )}
                />
                {errors.currency && (
                  <div className="text-danger small">{errors.currency.message}</div>
                )}
              </Form.Group>
            </Col>

            {/* Estimated Price - Writable */}
            <Col md={3}>
              <Form.Group>
                <Form.Label>{getDynamicLabel("Estimated Price")}</Form.Label>
                <Controller
                  name="estimated_price"
                  control={control}
                  render={({ field }) => {
                    const formattedValue = field.value
                      ? formatWithCommasAndDecimals(field.value)
                      : "";

                    return (
                      <Form.Control
                        type="text"
                        value={formattedValue}
                        placeholder="Enter estimated price"
                        onChange={(e) => {
                          const raw = parseRawNumber(e.target.value);
                          field.onChange(raw);
                        }}
                      />
                    );
                  }}
                />
              </Form.Group>
            </Col>

            {/* Price per Unit - Writable */}
            <Col md={3}>
              <Form.Group>
                <Form.Label>{getDynamicLabel("Price per Unit")}</Form.Label>
                <Controller
                  name="price_per_unit"
                  control={control}
                  render={({ field }) => {
                    const formattedValue = field.value
                      ? formatWithCommasAndDecimals(field.value)
                      : "";

                    return (
                      <Form.Control
                        type="text"
                        value={formattedValue}
                        placeholder="Enter price per unit"
                        onChange={(e) => {
                          const raw = parseRawNumber(e.target.value);
                          field.onChange(raw);
                        }}
                      />
                    );
                  }}
                />
              </Form.Group>
            </Col>
          </Row>
          <hr className="my-4" />
        </>
      )}
      <Row className="g-3">
        {/* Minimum Asking Price */}
        <Col md={12}>
          <Form.Group>
            <Form.Label>{getDynamicLabel("Maximum Buying Price", true)}</Form.Label>
            <Controller
              name="max_buying_price"
              control={control}
              render={({ field }) => {
                const formattedValue = field.value
                  ? formatWithCommasAndDecimals(field.value)
                  : "";

                return (
                  <Form.Control
                    type="text"
                    value={formattedValue}
                    placeholder="Enter maximum buying price"
                    onChange={(e) => {
                      const raw = parseRawNumber(e.target.value);
                      field.onChange(raw);
                    }}
                  />
                );
              }}
            />
            {errors.minimum_asking_price && (
              <div className="text-danger small">
                {errors.minimum_asking_price.message}
              </div>
            )}
          </Form.Group>
        </Col>

        {/* <Col md={6}>
          <Form.Group>
            <Form.Label>Discount (%)</Form.Label>
            <Controller
              name="discount_percentage"
              control={control}
              render={({ field }) => {
                const formattedValue = field.value
                  ? formatWithCommasAndDecimals(field.value)
                  : "";

                return (
                  <Form.Control
                    type="text"
                    value={formattedValue}
                    placeholder="Enter discount"
                    onChange={(e) => {
                      const raw = parseRawNumber(e.target.value);
                      field.onChange(raw);
                    }}
                  />
                );
              }}
            />
            {errors.minimum_asking_price && (
              <div className="text-danger small">
                {errors.minimum_asking_price.message}
              </div>
            )}
          </Form.Group>
        </Col> */}
      </Row>
    </div>
  );
};

export default React.memo(OfferPricing);

// const isReverseAuction = watch("is_reverse_auction");
// const allowBidding = watch("allow_bidding");
// console.log("bids allowBidding", allowBidding);
// {
//   /* Listing Type */
// }
// {
//   /* <Row className="mb-4">
//         <Col>
//           <Form.Label className="d-block">Listing Type</Form.Label>
//           <Controller
//             name="allow_bidding"
//             control={control}
//             render={({ field }) => (
//               <div className="d-flex gap-3">
//                 <Form.Check
//                   type="radio"
//                   id="create-bid"
//                   label="Create Bid"
//                   value={1}
//                   checked={field.value === 1}
//                   onChange={(e) =>
//                     field.onChange(Number(e.target.value))
//                   }
//                 />
//                 <Form.Check
//                   type="radio"
//                   id="sell-product"
//                   label="Sell as Product"
//                   value={0}
//                   checked={field.value === 0}
//                   onChange={(e) =>
//                     field.onChange(Number(e.target.value))
//                   }
//                 />
//               </div>
//             )}
//           />
//         </Col>
//       </Row> */
// }

// {
//   /* Bid Increment */
// }
// {
//   /* <Col md={3}>
//           <Form.Group>
//             <Form.Label>Bid Increment (USD)</Form.Label>
//             <Controller
//               name="bid_increment"
//               control={control}
//               render={({ field }) => {
//                 const formattedValue = field.value
//                   ? formatWithCommasAndDecimals(field.value)
//                   : "";

//                 return (
//                   <Form.Control
//                     type="text"
//                     value={formattedValue}
//                     placeholder="Enter bid increment"
//                     disabled={allowBidding !== 1}
//                     onChange={(e) => {
//                       const raw = parseRawNumber(e.target.value);
//                       field.onChange(raw);
//                     }}
//                   />
//                 );
//               }}
//             />
//             {errors.bid_increment && (
//               <div className="text-danger small">
//                 {errors.bid_increment.message}
//               </div>
//             )}
//           </Form.Group>
//         </Col> */
// }

// {
//   /* Bid Closed At */
// }
// {
//   /* <Col md={3}>
//           <Form.Group>
//             <Form.Label>Bid Closed At</Form.Label>
//             <Form.Control
//               type="datetime-local"
//               {...register("bid_closed_at")}
//               disabled={allowBidding !== 1}
//             />
//             {errors.bid_closed_at && (
//               <div className="text-danger small">
//                 {errors.bid_closed_at.message}
//               </div>
//             )}
//           </Form.Group>
//         </Col> */
// }

// {
//   /* Auction Status */
// }
// {
//   /* <Col md={3}>
//           <Form.Group>
//             <Form.Label>Auction Status</Form.Label>
//             <Controller
//               name="auction_status"
//               control={control}
//               render={({ field }) => (
//                 <Form.Select {...field} disabled>
//                   <option value="open">Open</option>
//                 </Form.Select>
//               )}
//             />
//           </Form.Group>
//         </Col> */
// }
